<template>
  <div>
    <v-sheet elevation="1">
      <v-toolbar dense color="white">
        <v-toolbar-title>{{ screenTitle }}</v-toolbar-title>
      </v-toolbar>
      <v-container fluid>
        <error-message :error-object="errorObject" />
        <success-message :success-object="successObject" />
        <p style="margin: 5px 0 15px 0">
          平時の安定ヨウ素剤の配布状況を更新するには、<br />
          安定ヨウ素剤配布管理システムより出力されたＣＳＶデータを、下よりアップロードしてください。<br />
          アップロード後はクロノロジー情報一覧画面にて、登録結果を確認してください。
        </p>
        <v-row>
          <v-col cols="12" sm="10">
            <!-- 住民基本情報ファイル選択 -->
            <v-file-input
              v-model="csvFiles.infoFile"
              accept=".csv"
              label="住民基本情報"
              :placeholder="config.PLACEHOLDER_FILE"
              prepend-icon="mdi-paperclip"
              outlined
            >
              <template v-slot:selection="{ text }">
                <v-chip small label color="primary">{{ text }}</v-chip>
              </template>
            </v-file-input>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="10">
            <!-- 配布管理台帳ファイル選択 -->
            <v-file-input
              v-model="csvFiles.distFile"
              accept=".csv"
              label="配布管理台帳"
              :placeholder="config.PLACEHOLDER_FILE"
              prepend-icon="mdi-paperclip"
              outlined
            >
              <template v-slot:selection="{ text }">
                <v-chip small label color="primary">{{ text }}</v-chip>
              </template>
            </v-file-input>
          </v-col>
        </v-row>
        <v-divider class="mb-1"></v-divider>
        <v-row>
          <v-spacer></v-spacer>
          <!-- アップロードボタン -->
          <v-btn
            class="mx-2"
            color="primary"
            :disabled="!canUpload"
            :loading="loading"
            @click="handleUpload"
          >
            アップロード
          </v-btn>
        </v-row>
        <ErrorDialog ref="errorDialog" />
        <v-dialog v-model="notificationDialog" persistent max-width="680">
          <v-card>
            <v-card-title></v-card-title>
            <v-card-text>
              安定ヨウ素剤事前配布の登録処理を受付けましたが、登録はまだ完了しておりません。<br />
              時間をおいて、登録結果の成否をクロノロジー情報一覧にて確認してください。<br />
              登録失敗している場合は再度アップロードしてください。<br />
              <div class="note">
                ※メニュー画面よりクロノロジー情報一覧へ遷移できない場合は<br />
                システム管理者へお問い合わせください。
              </div>
              <div class="note">
                ※本システムをログアウトまたは閉じた場合でも、取込処理が停止することはありません。
              </div>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                class="mx-2"
                color="primary"
                @click="notificationDialog = false"
              >
                OK
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-container>
    </v-sheet>
    <v-overlay :value="overlay" opacity="1" color="white">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import ErrorDialog from "@/components/ErrorDialog";
import messageUtils from "@/mixins/message-utils";
import config from "@/js/const";
import { RepositoryFactory } from "@/js/repository/RepositoryFactory";
import { ScreenId } from "@/js/value-objects/screen-id";

const lodineTabletsPreDistUploadRepository = RepositoryFactory.get(
  "lodineTabletsPreDistUpload"
);

// 住民基本情報ファイルの項目名
const INFO_FIELDS = [
  // 市町村コード
  "municip_code",
  // 住民区分
  "resident_cls",
  // 対象者番号
  "person_no",
  // 漢字氏名
  "name_kanji",
  // カナ氏名
  "name_kana",
  // 性別
  "sex",
  // 生年月日
  "birthday",
  // 年齢
  "age",
  // 郵便番号
  "postal_code",
  // 市町村名
  "municip_name",
  // 住所１
  "address_1",
  // 住所２
  "address_2",
  // 方書
  "additional_address",
  // 町内会コード
  "town_community_code",
  // 世帯番号
  "household_no",
  // 世帯主漢字氏名
  "household_name_kanji",
  // 世帯主カナ氏名
  "household_name_kana",
  // 申請区分
  "request_cls",
  // 現況コード
  "status_code"
];

// 配布管理台帳ファイルの項目名
const DIST_FIELDS = [
  // 市町村コード
  "municip_code",
  // 対象者番号
  "person_no",
  // 連番
  "serial_number",
  // 対象外フラグ
  "target_flg",
  // 対象外理由
  "target_reason",
  // 説明会番号
  "seminar_no",
  // 開催年度
  "year",
  // 受付会場名
  "reception_venue_name",
  // 受付日付
  "reception_date",
  // 薬剤配布受付会場名
  "dist_reception_venue_name",
  // 薬剤配布受付日時
  "dist_reception_date",
  // 薬剤配布日
  "distribution_date",
  // 薬剤種別1
  "distribution_type_1",
  // 薬剤配布数量1
  "distribution_volume_1",
  // 薬剤有効期限1
  "expiration_date_1",
  // 薬剤種別2
  "distribution_type_2",
  // 薬剤配布数量2
  "distribution_volume_2",
  // 薬剤有効期限2
  "expiration_date_2",
  // 服用不適格
  "take_failure",
  // 不配布理由
  "undistribution_reason",
  // 薬剤返却日
  "return_date",
  // 薬剤返却理由
  "return_reason",
  // 薬剤紛失日
  "lost_date",
  // 問診票印刷日時
  "interview_print_date",
  // 問診票回収日
  "interview_recall_date",
  // 配布区分
  "distribution_cls",
  // 住民区分
  "resident_cls",
  // 年令
  "age",
  // 備考１
  "remarks_1",
  // 備考２
  "remarks_2",
  // 備考３
  "remarks_3",
  // 特例フラグ
  "special_case_flg",
  // 無効フラグ
  "invalid_flg"
];

/**
 * FileオブジェクトがCSVファイルを参照しているかどうかを検査する。
 * @param {File} file 検査対象のFileオブジェクト
 * @returns {boolean} 検査が成功した場合 true、その他の場合 false。
 */
const isValidCSVFile = file =>
  file?.name.toLowerCase().endsWith(".csv") ?? false;

/**
 * Fileオブジェクトのテキスト読込みが完了した時に成功するPromiseを作成する。
 * @param {File} file 読込み対象のFileオブジェクト
 * @param {string} [encoding=Shift_JIS] エンコーディング
 * @returns {Promise} Fileオブジェクトのテキスト読込みが完了した時に成功するPromise
 */
const readTextFile = (file, encoding = "Shift_JIS") =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = event => resolve(event.target.result);
    reader.onerror = event => reject(event.target.error);
    reader.readAsText(file, encoding);
  });

/**
 * CSVの行を分割する関数を作成する。
 * この関数は、二重引用符等でエスケープされた区切り文字を考慮しない。
 * @param {string} [separator=\r\n] 行の区切り文字
 * @returns 区切り文字にしたがってテキストを分割する関数
 */
const getCSVRowSplitter = (separator = "\r\n") => text => {
  // 最後の文字が区切り文字の場合、splitした配列の末尾に不要な空行ができるため除去する。
  if (text.endsWith(separator)) {
    text = text.slice(0, -separator.length);
  }
  return text.split(separator);
};

/**
 * CSVの列を分割する関数を作成する。
 * この関数は、二重引用符等でエスケープされた区切り文字を考慮しない。
 * @param {string} [separator=,] 列の区切り文字
 * @returns 区切り文字にしたがってテキストを分割する関数
 */
const getCSVColSplitter = (separator = ",") => text => text.split(separator);

/**
 * CSVテキストを行と列の配列に変換する関数を作成する。
 * @param rowSplitter 行分割関数
 * @param colSplitter 列分割関数
 * @returns CSVテキストを行と列の配列に変換する関数
 */
const getCSVParser = (
  rowSplitter = getCSVRowSplitter(),
  colSplitter = getCSVColSplitter()
) => text => rowSplitter(text).map(colSplitter);

/**
 * 列名配列とレコード配列からオブジェクトを作成する関数を作成する。
 * @param {string[]} fields 列名配列
 * @returns レコード配列を引数にオブジェクトを作成する関数
 */
const getMapper = fields => record =>
  Object.fromEntries(record.map((value, index) => [fields[index], value]));

export default {
  mixins: [messageUtils],

  components: {
    ErrorDialog,
    ErrorMessage: () => import("@/components/ErrorMessage"),
    SuccessMessage: () => import("@/components/SuccessMessage")
  },

  data: () => ({
    screenTitle: ScreenId.getScreenTitle(ScreenId.SF1903),
    notificationDialog: false,
    loading: false,
    overlay: false,
    config: config,

    csvFiles: {
      // 住民基本情報ファイル
      infoFile: null,
      // 配布管理台帳ファイル
      distFile: null
    }
  }),

  computed: {
    /**
     * アップロード可能かどうかを判定する。
     * @returns {bool} 住民基本情報ファイルと配布管理台帳ファイルの両方が選択されている場合 true、その他の場合 false。
     */
    canUpload() {
      return !!(this.csvFiles.infoFile && this.csvFiles.distFile);
    }
  },

  watch: {
    $route: "initialize"
  },

  created() {
    this.initialize();
  },

  mounted() {
    this.pageTitle(this.screenTitle);
  },

  methods: {
    /**
     * コンポーネントを初期化する。
     */
    initialize() {
      this.overlay = true;
      this.loading = true;

      this.notificationDialog = false;
      this.csvFiles.infoFile = null;
      this.csvFiles.distFile = null;

      this.overlay = false;
      this.loading = false;
    },

    /**
     * アップロードボタンのクリックイベントを処理する。
     */
    async handleUpload() {
      this.clearMessage();

      this.loading = true;
      try {
        // CSVファイル形式チェック
        if (!Object.values(this.csvFiles).every(isValidCSVFile)) {
          await this.showErrorDialog(
            "ファイル形式が正しくありません。",
            "CSVファイルを選択してください。"
          );
          return;
        }

        // 住民基本情報ファイルのチェックと読込み
        const [isValidInfo, infoData] = await this.validateAndReadCSV(
          this.csvFiles.infoFile,
          INFO_FIELDS
        );
        if (!isValidInfo) {
          await this.showErrorDialog(
            "ファイルの読み込みに失敗しました。",
            "システム管理者に問い合わせて下さい。"
          );
          return;
        }

        // 配布管理台帳ファイルのチェックと読込み
        const [isValidDist, distData] = await this.validateAndReadCSV(
          this.csvFiles.distFile,
          DIST_FIELDS
        );
        if (!isValidDist) {
          await this.showErrorDialog(
            "ファイルの読み込みに失敗しました。",
            "システム管理者に問い合わせて下さい。"
          );
          return;
        }

        // ファイルをアップロード
        await this.upload(infoData, distData);
      } finally {
        this.loading = false;
      }
    },

    /**
     * CSVファイルを検証して読込む。
     * @param {File} file CSVファイル
     * @param {string[]} fields CSVファイルの列名配列
     * @returns {[boolean, Object[]]} 検証の結果と読込んだデータ
     */
    async validateAndReadCSV(file, fields) {
      // 検証失敗時の戻り値
      const failure = [false, null];

      // CSVファイルを二次元配列として読み込む
      let records;
      try {
        records = getCSVParser()(await readTextFile(file));
      } catch (error) {
        return failure;
      }

      // 配列長チェック
      if (
        records.length === 0 ||
        records.some(record => record.length != fields.length)
      ) {
        return failure;
      }

      // ヘッダ行を削除
      const skipRows = 1;
      const dataRecords = records.slice(skipRows);

      // 二次元配列をオブジェクト配列に変換
      return [true, dataRecords.map(getMapper(fields))];
    },

    /**
     * CSVデータをアップロードする
     * @param {Object[]} infoData 住民基本情報
     * @param {Object[]} distData 配布管理台帳
     */
    async upload(infoData, distData) {
      try {
        // 安定ヨウ素剤事前配布状況登録APIを呼び出す
        const response = await lodineTabletsPreDistUploadRepository.register(
          ScreenId.SF1903,
          infoData,
          distData
        );
        this.handleSuccess(response);
        this.notificationDialog = true;
      } catch (error) {
        this.handleUpdateError(error);
      }
    },

    /**
     * エラーダイアログを表示する。
     * @param {...string} messages メッセージ
     */
    async showErrorDialog(...messages) {
      await this.$refs.errorDialog.open(messages);
    }
  }
};
</script>

<style scoped>
.note {
  text-indent: -1em;
  padding-left: 1em;
}
</style>
